import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { Count, CountWrap, Text } from '@/components/dealCatalog-page/allocator-page/modals/submitTo';
import { PROPOSAL_TYPE } from '@/types/investments';
import { useMutation } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ClientEntityDetailsAllocationsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { ALLOCATOR_PAGE_CREATE_ALLOCATIONS_MUTATION } from '@/components/dealCatalog-page/allocator-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { currencyFormatter } from '@/shared/utils/currency';
import { themeVar } from '@/styles/theme';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
  INVESTOR_DETAILS_PAGE_DETAILS_SECTION_GROUP_BY_PRIMARY_ENTITY_ID_QUERY,
  INVESTOR_DETAILS_PAGE_DETAILS_SECTION_LEGAL_ENTITY_QUERY
} from '../../../details/queries';
import { INVESTOR_DETAILS_GROUP_ASSET_CLASS_TARGETS_QUERY, INVESTOR_DETAILS_LEGAL_ENTITY_ASSET_CLASS_TARGETS_QUERY } from '../../../queries';
import { InvestorDetailsEntityTypeEnum } from '../../../types';
import { BEGIN_ALLOCATION_TAB_TABLE_DATA_QUERY } from '../queries';

interface EditAmountProps {
  isOpen: boolean;
  entityType: InvestorDetailsEntityTypeEnum;
  row: ClientEntityDetailsAllocationsQuery['ClientEntityDetailsAllocations']['allocations'][number] | null;
  biteSize: { ignoreBiteSize: boolean; fundBiteSize: number; directBiteSize: number };
  groupedEntitiesSelectOptions: ISelectValue[];
  onClose: () => void;
}

const EditAmount = ({ isOpen, entityType, row, biteSize, groupedEntitiesSelectOptions, onClose }: EditAmountProps) => {
  const params = useParams();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { handleSubmit, setValue, control, watch } = useForm<{ amount: string; legalEntityId: string }>({
    defaultValues: { amount: '', legalEntityId: '' }
  });

  // Watch the legalEntityId field to use in validation
  const selectedLegalEntityId = watch('legalEntityId');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [filteredGroupedEntitiesSelectOptions, setFilteredGroupedEntitiesSelectOptions] = useState<ISelectValue[]>([]);

  const [createAllocations, { loading: createAllocationsLoading }] = useMutation(ALLOCATOR_PAGE_CREATE_ALLOCATIONS_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      BEGIN_ALLOCATION_TAB_TABLE_DATA_QUERY,
      INVESTOR_DETAILS_LEGAL_ENTITY_ASSET_CLASS_TARGETS_QUERY,
      INVESTOR_DETAILS_GROUP_ASSET_CLASS_TARGETS_QUERY,
      INVESTOR_DETAILS_PAGE_DETAILS_SECTION_LEGAL_ENTITY_QUERY,
      INVESTOR_DETAILS_PAGE_DETAILS_SECTION_GROUP_BY_PRIMARY_ENTITY_ID_QUERY
    ]
  });

  useEffect(() => {
    if (!row || entityType === InvestorDetailsEntityTypeEnum.Group) return;

    setValue('amount', (biteSize.ignoreBiteSize ? row.minimum?.toString() : row.committedCapital?.toString()) || '');
  }, [row]);

  useEffect(() => {
    if (entityType === InvestorDetailsEntityTypeEnum.Group) return;
    setValue('legalEntityId', params?.entityId || '');
  }, [entityType, params?.entityId]);

  useEffect(() => {
    if (entityType !== InvestorDetailsEntityTypeEnum.Group) return;
    setFilteredGroupedEntitiesSelectOptions(
      groupedEntitiesSelectOptions?.filter((option) => !row?.legalEntitiesNotEligibleForAllocation?.includes(option.value)) || []
    );
  }, [groupedEntitiesSelectOptions, row, entityType]);

  const saveValue = async (data: { amount: string; legalEntityId: string }) => {
    if (!isConfirmationDialogOpen) {
      setIsConfirmationDialogOpen(true);
      return;
    }

    try {
      setErrorMessage(null);
      const { legalEntityId, amount } = data;

      await createAllocations({
        variables: {
          data: [
            {
              legalEntityId,
              investmentId: row.investmentId,
              committedCapital: parseFloat(amount),
              allocationStatus: 'Outstanding',
              proposalType: row.proposalTypeByLegalEntity.find((p) => p.legalEntityId === legalEntityId)?.proposalType,
              suitability: row.suitability,
              decision: true,
              reason: ''
            }
          ]
        }
      });

      onClose();
    } catch (error) {
      console.error(error);
      setIsConfirmationDialogOpen(false);
      setErrorMessage(defaultErrorMessage);
    }
  };

  const getLegalEntityValues = (legalEntityId: string) => {
    const legalEntity = row?.proposalTypeByLegalEntity?.find((legalEntity) => legalEntity.legalEntityId === legalEntityId);
    return {
      proposalType: legalEntity?.proposalType,
      committedCapital: legalEntity?.committedCapital
    };
  };

  const handleGroupedEntityAmount = (entityId: string) => {
    const { committedCapital } = getLegalEntityValues(entityId);
    const amountValue = committedCapital?.toString() || '';
    setValue('amount', amountValue);
  };

  if (!row) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title={!isConfirmationDialogOpen ? 'Edit Investment Amount' : 'Confirmation'}
      confirmButton={{
        name: isConfirmationDialogOpen ? 'Confirm' : 'Confirm Allocation',
        onClick: handleSubmit(saveValue),
        disabled: createAllocationsLoading
      }}
      showCancelButton={!createAllocationsLoading}
      showCloseIcon={!createAllocationsLoading}
      isUseOutsideClick={!createAllocationsLoading}
      showRequiredFields={!createAllocationsLoading && !isConfirmationDialogOpen}
    >
      <AnimatePresence>
        {isConfirmationDialogOpen ? (
          <motion.div
            initial={{
              opacity: 0
            }}
            animate={{
              opacity: 1
            }}
          >
            {createAllocationsLoading ? (
              <ModalLoader />
            ) : (
              <ModalBody>
                <Text fontWeight="400">Please review.</Text>
                <Text fontWeight="400">
                  <CountWrap>
                    <Count>1</Count>
                  </CountWrap>
                  allocation will be initiated.
                </Text>
              </ModalBody>
            )}
          </motion.div>
        ) : (
          <>
            {errorMessage && (
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            )}
            <FieldsWrapper>
              {entityType === InvestorDetailsEntityTypeEnum.Group && (
                <Controller
                  name="legalEntityId"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Investor Entity is required'
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      options={filteredGroupedEntitiesSelectOptions}
                      setValue={(entityId) => {
                        onChange(entityId);
                        handleGroupedEntityAmount(entityId);
                      }}
                      label="Investor Entity"
                      errorText={error?.message}
                      size="lg"
                      tooltip="Select the entity to use for this subscription. This is the entity that will appear on the subscription documents."
                      tooltipWidth={400}
                      isRequiredField
                    />
                  )}
                />
              )}
              {((entityType === InvestorDetailsEntityTypeEnum.Group && !!watch('legalEntityId')) ||
                entityType !== InvestorDetailsEntityTypeEnum.Group) && (
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Amount is required'
                    },
                    min: {
                      value: 1,
                      message: 'Amount must be greater than 0'
                    },
                    validate: (value) => {
                      const amount = parseFloat(value);
                      const currentLegalEntityId =
                        entityType === InvestorDetailsEntityTypeEnum.Group ? selectedLegalEntityId : params?.entityId || '';

                      const { proposalType } = getLegalEntityValues(currentLegalEntityId);

                      if (proposalType === PROPOSAL_TYPE.INITIAL && amount < row.minimum) {
                        return 'Cannot be less than fund minimum';
                      }
                      if (proposalType === PROPOSAL_TYPE.INCREASE && amount <= 0) {
                        return 'Cannot be less than 0';
                      }
                      return true;
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      value={value ? formatMaskedDollarInputValue(value) : ''}
                      onChange={(e) => {
                        const formattedValue = formatDollarInputValue(e.target.value);
                        onChange(formattedValue);
                      }}
                      label={(() => {
                        const currentLegalEntityId =
                          entityType === InvestorDetailsEntityTypeEnum.Group ? selectedLegalEntityId : params?.entityId || '';

                        const { proposalType } = getLegalEntityValues(currentLegalEntityId);
                        return proposalType === PROPOSAL_TYPE.INCREASE ? 'Follow-on Investment Amount' : 'Initial Investment Amount';
                      })()}
                      errorText={error?.message}
                      styleType={value ? 'success' : 'default'}
                      isRequiredField
                      withDollarSign
                      autoFocus
                      description={(() => {
                        const currentLegalEntityId =
                          entityType === InvestorDetailsEntityTypeEnum.Group ? selectedLegalEntityId : params?.entityId || '';

                        const { proposalType } = getLegalEntityValues(currentLegalEntityId);
                        return proposalType === PROPOSAL_TYPE.INCREASE
                          ? ''
                          : `Initial Investment minimum: ${currencyFormatter.format(row?.minimum || 0)}`;
                      })()}
                    />
                  )}
                />
              )}
            </FieldsWrapper>
          </>
        )}
      </AnimatePresence>
    </ModalWrappedContent>
  );
};

export default EditAmount;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
